/**
 * Shared appsCode (hospital tenant) helpers.
 * Never treat the strings "undefined" / "null" as valid tenant codes.
 */

import LocalStorageService from "app/service/localStorageService";
import { readTenantContext } from "app/utils/tenantHost";

const APPS_CODE_STORAGE_KEY = "appsCode";

export function isValidAppsCode(value) {
  if (value == null) return false;
  const s = String(value).trim();
  if (!s) return false;
  const lower = s.toLowerCase();
  return lower !== "undefined" && lower !== "null";
}

/**
 * @returns {string|null} trimmed appsCode or null when missing/invalid
 */
export function sanitizeAppsCode(value, fallback) {
  if (isValidAppsCode(value)) return String(value).trim();
  if (isValidAppsCode(fallback)) return String(fallback).trim();
  return null;
}

/**
 * Prefer authenticated user appsCode, then hostname/local tenant context.
 */
export function resolveAppsCodeFromUser(user, { readTenant, getStoredAppsCode } = {}) {
  const fromUser = sanitizeAppsCode(user?.appsCode);
  if (fromUser) return fromUser;

  try {
    const read = typeof readTenant === "function" ? readTenant : readTenantContext;
    const tenant = read();
    const fromTenant = sanitizeAppsCode(tenant?.appsCode);
    if (fromTenant) return fromTenant;
  } catch {
    // ignore
  }

  try {
    const getStored =
      typeof getStoredAppsCode === "function"
        ? getStoredAppsCode
        : () => LocalStorageService.getService().getItem(APPS_CODE_STORAGE_KEY);
    return sanitizeAppsCode(getStored());
  } catch {
    return null;
  }
}

/** Resolve current hospital appsCode from user + persisted tenant context. */
export function resolveCurrentAppsCode(user) {
  return resolveAppsCodeFromUser(user);
}
